import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { generateSummary } from '../utils';
import { IUserGroup, IUserFilterItem, IUseFilterGroupProps, IUseFilterGroupReturn } from '../type';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash-es';

export const useFilterGroup = ({
  value,
  defaultName,
  defaultNameEn,
  defaultNameCn,
  max,
  onChange,
  setIsConfirm,
}: IUseFilterGroupProps): IUseFilterGroupReturn => {
  const fallbackName = defaultName || defaultNameEn || defaultNameCn;
  // 创建空的筛选器项
  const createEmptyFilter = (): IUserFilterItem => ({
    id: `filter_${uuidv4()}`,
    field: undefined,
    fieldType: undefined,
    operator: undefined,
    value: undefined,
    hasError: true,
  });

  // 初始化用户组数据
  const initValue: IUserGroup = useMemo(
    () => {
      if (value) {
        // 如果外部传入的 value 存在，使用它
        // 但如果 filters 为空，确保至少有一个空的筛选器
        return {
          ...value,
          filters: value.filters && value.filters.length > 0 ? value.filters : [createEmptyFilter()],
        };
      }
      // 如果 value 不存在，创建默认值，包含一个空的筛选器
      return {
        id: `group_${uuidv4()}`,
        name: fallbackName,
        name_en: defaultNameEn,
        name_cn: defaultNameCn,
        isNameCustomized: false,
        filters: [createEmptyFilter()],
        logic: 'AND',
        summaryEn: defaultNameEn,
        summaryCn: defaultNameCn,
      };
    },
    [defaultNameCn, defaultNameEn, fallbackName, value]
  );

  const [currentValue, setCurrentValue] = useState<IUserGroup>(initValue);
  const prevValueRef = useRef(value);

  // 当外部 value 变化时更新内部状态
  useEffect(() => {
    if (!isEqual(prevValueRef.current, value) && value) {
      prevValueRef.current = value;
      // 如果外部 value 的 filters 为空，确保至少有一个空的筛选器
      const updatedValue = {
        ...value,
        filters: value?.filters && value.filters.length > 0 ? value?.filters : [createEmptyFilter()],
      };
      setCurrentValue(updatedValue);
    }
  }, [value]);

  // 更新用户组并触发回调
  const updateGroup = useCallback(
    (updates: Partial<IUserGroup>) => {
      const updatedGroup: IUserGroup = {
        ...currentValue,
        ...updates,
      };
      // 如果不是自定义名称，则自动生成名称和摘要
      if (!updatedGroup.isNameCustomized) {
        const summaryEn = generateSummary(updatedGroup.filters, updatedGroup.logic, defaultNameEn, true);
        const summaryCn = generateSummary(updatedGroup.filters, updatedGroup.logic, defaultNameCn, false);
        updatedGroup.summaryEn = summaryEn;
        updatedGroup.summaryCn = summaryCn;
        updatedGroup.name = summaryEn;
        updatedGroup.name_en = summaryEn;
        updatedGroup.name_cn = summaryCn;
      } else {
        updatedGroup.name_en = updatedGroup.name;
        updatedGroup.name_cn = updatedGroup.name;
      }
      setCurrentValue(updatedGroup);
      onChange?.(updatedGroup);
      setIsConfirm?.(false);
    },
    [currentValue, defaultNameCn, defaultNameEn, onChange, setIsConfirm]
  );

  // 处理筛选条件变化
  const handleFilterChange = useCallback(
    (filterId: string, updates: Partial<IUserFilterItem>) => {
      const updatedFilters = currentValue.filters.map((filter) => {
        if (filter.id === filterId) {
          const updated = { ...filter, ...updates };
          // 验证条件完整性
          const isBetweenError = updated.operator === 'between' && (!updated.value?.[0] || !updated.value?.[1]);
          updated.hasError = !updated.field || !updated.operator || updated.value === undefined || isBetweenError;
          return updated;
        }
        return filter;
      });
      updateGroup({ filters: updatedFilters });
    },
    [currentValue.filters, updateGroup]
  );

  // 添加新筛选条件
  const handleAddFilter = useCallback(() => {
    if (currentValue.filters.length >= max) {
      return;
    }

    const newFilter = createEmptyFilter();
    updateGroup({ filters: [...currentValue.filters, newFilter] });
  }, [currentValue.filters, max, updateGroup]);

  // 删除筛选条件
  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const updatedFilters = currentValue.filters.filter((f) => f.id !== filterId);

      // 如果删除后没有条件，保留一个空的筛选器
      if (updatedFilters.length === 0) {
        updateGroup({
          filters: [createEmptyFilter()],
          name: fallbackName,
          name_en: defaultNameEn,
          name_cn: defaultNameCn,
          isNameCustomized: false,
          summaryEn: defaultNameEn,
          summaryCn: defaultNameCn,
        });
      } else {
        updateGroup({ filters: updatedFilters });
      }
    },
    [currentValue.filters, defaultNameCn, defaultNameEn, fallbackName, updateGroup]
  );

  // 清除所有筛选条件
  const handleClear = useCallback(() => {
    updateGroup({
      filters: [createEmptyFilter()],
      name: fallbackName,
      name_en: defaultNameEn,
      name_cn: defaultNameCn,
      isNameCustomized: false,
      summaryEn: defaultNameEn,
      summaryCn: defaultNameCn,
    });
  }, [defaultNameCn, defaultNameEn, fallbackName, updateGroup]);

  // 切换逻辑运算符
  const handleLogicChange = useCallback(
    (logic: 'AND' | 'OR') => {
      updateGroup({ logic });
    },
    [updateGroup]
  );

  return {
    currentValue,
    fallbackName,
    createEmptyFilter,
    updateGroup,
    handleFilterChange,
    handleAddFilter,
    handleRemoveFilter,
    handleClear,
    handleLogicChange,
  };
};

